import { requisitar } from '@/lib/http';
import type { RespostaDeLogin, RespostaDeRecuperacao } from '@/types/api';

/**
 * Endpoints de autenticacao. Especificacao V3.1, item 15.
 *
 * Todas as chamadas daqui passam `semRenovacao`, porque sao elas proprias o
 * mecanismo de renovacao: deixar o interceptor agir sobre um 401 destes
 * endpoints criaria recursao.
 */

const BASE = '/api/autenticacao';

/** Autentica por e-mail e senha. */
export function entrar(
  email: string,
  senha: string,
  sinal?: AbortSignal,
): Promise<RespostaDeLogin> {
  return requisitar<RespostaDeLogin>(`${BASE}/login`, {
    metodo: 'POST',
    corpo: { email, senha },
    semRenovacao: true,
    ...(sinal === undefined ? {} : { sinal }),
  });
}

/**
 * Renova a sessao rotacionando o refresh token.
 *
 * SEM CORPO. O token vai no cookie HttpOnly, que o navegador anexa sozinho — a SPA
 * nao tem o valor e nao deve ter. O servidor aceita o corpo tambem, para clientes
 * que nao sao navegador.
 *
 * A rotacao acontece no servidor: se a resposta chegou la, o token anterior ja foi
 * revogado e o cookie foi substituido, com ou sem sucesso do lado do cliente.
 */
export function renovar(): Promise<RespostaDeLogin> {
  return requisitar<RespostaDeLogin>(`${BASE}/renovar`, {
    metodo: 'POST',
    semRenovacao: true,
  });
}

/**
 * Encerra a sessao, revogando a familia inteira no servidor.
 *
 * Sempre responde 204, inclusive quando nao havia cookie, e o servidor apaga o
 * cookie de qualquer forma.
 */
export function sair(): Promise<void> {
  return requisitar<void>(`${BASE}/logout`, {
    metodo: 'POST',
    semRenovacao: true,
  });
}

/**
 * Altera a senha do usuario autenticado.
 *
 * Exige a senha atual, e o identificador do usuario vem do token — nunca do
 * corpo. Trocar a senha derruba todas as sessoes abertas, inclusive esta: quem
 * chama precisa conduzir o usuario a um novo login.
 */
export function alterarSenha(senhaAtual: string, novaSenha: string): Promise<void> {
  return requisitar<void>(`${BASE}/alterar-senha`, {
    metodo: 'POST',
    corpo: { senhaAtual, novaSenha },
    semRenovacao: true,
  });
}

/** Explica como recuperar a senha nesta versao. Decisao 12. */
export function obterInstrucaoDeRecuperacao(sinal?: AbortSignal): Promise<RespostaDeRecuperacao> {
  return requisitar<RespostaDeRecuperacao>(`${BASE}/recuperar-senha`, {
    semRenovacao: true,
    ...(sinal === undefined ? {} : { sinal }),
  });
}
